import React, { useEffect, useState } from 'react';
import { Spin, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import PPAOnboardingHeader from 'components/PPAOnboardingHeader';
import ActivateForm from './components/ActivateForm';
import { getUrlParam } from 'utils/common';
import { getTeamMemberInviteInfo } from 'api/public';
import { PATH } from 'constants/path';
import type { TInviteInfo } from 'types/invite';
import { ERoleCode } from 'types/enumItems';
import s from './s.module.less';

const ActivateAccountPage: React.FC = () => {
    const token = getUrlParam('token') || '';
    const [loading, setLoading] = useState<boolean>(!!token);
    const [inviteInfo, setInviteInfo] = useState<TInviteInfo | null>(null);
    const [isExistingUser, setIsExistingUser] = useState<boolean>(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        (async () => {
            const res = await getTeamMemberInviteInfo(token);
            if (cancelled) return;
            const resWithCode = res as typeof res & { errorCode?: number };
            if (resWithCode.errorCode === 4007) {
                message.error('This invite link has already been used. Please sign in.');
                navigate(PATH.PPA_LOGIN, { replace: true });
                return;
            }
            const payload = (res?.data as { data?: TInviteInfo } | undefined)?.data;

            if (!res.error && payload) {
                if (payload.isExistingUser) {
                    setIsExistingUser(true);
                    // navigate(PATH.PPA_LOGIN, {
                    //     replace: true,
                    //     state: { email: payload?.invitee?.email || '' },
                    // });
                    // return;
                }
                setInviteInfo(payload);
            }
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [token, navigate]);

    const inviterName =
        inviteInfo?.inviter?.fullName ||
        [inviteInfo?.inviter?.firstName, inviteInfo?.inviter?.lastName].filter(Boolean).join(' ');
    const ownerName =
        inviteInfo?.owner?.fullName ||
        [inviteInfo?.owner?.firstName, inviteInfo?.owner?.lastName].filter(Boolean).join(' ');
    const ownerCredentials = inviteInfo?.owner?.credentials || inviteInfo?.owner?.title || '';
    const ownerAvatar = inviteInfo?.owner?.avatar || '';
    const providerName = inviterName || '{Provider Name}';
    const practiceName = inviteInfo?.practiceName || '';

    // The left-side onboarding steps differ by the invited member's role.
    const roleContent: { lead: string; bullets: string[] } = (() => {
        switch (inviteInfo?.roleCode) {
            case ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT:
                return {
                    lead: 'To start supporting front-desk operations on Klarity Health, follow these steps:',
                    bullets: ['Go to your dashboard to start managing scheduling, patient communication, and billing.'],
                };
            case ERoleCode.GROUP_CLINICAL_ASSISTANT:
                return {
                    lead: 'To start supporting patient care on Klarity Health, follow these steps:',
                    bullets: [
                        'Go to your dashboard to start supporting clinical documentation, labs, and prescriptions.',
                    ],
                };
            case ERoleCode.GROUP_PRACTICE_MANAGER:
                return {
                    lead: 'To start managing your practice on Klarity Health, follow these steps:',
                    bullets: ['Go to your dashboard to manage practice operations, billing, and team workflows.'],
                };
            case ERoleCode.GROUP_PROVIDER:
            default:
                return {
                    lead: 'To start seeing patients on Klarity Health, follow these steps:',
                    bullets: [
                        'Confirm your services.',
                        'Set your availability.',
                        'Complete your profile to showcase your expertise.',
                    ],
                };
        }
    })();

    return (
        <div className={s.pageWrap}>
            <div className={s.bgCircleLarge} />
            <div className={s.bgCircleSmall2} />
            <PPAOnboardingHeader showLogout={false} />
            <Spin spinning={loading}>
                <div className={s.content}>
                    <div className={s.leftSection}>
                        <div className={s.providerCard}>
                            {ownerAvatar ? (
                                <img className={s.avatar} src={ownerAvatar} alt={ownerName || 'owner'} />
                            ) : (
                                <div className={s.avatar} />
                            )}
                            <div className={s.providerMeta}>
                                {ownerName
                                    ? `${ownerName}${ownerCredentials ? `, ${ownerCredentials}` : ''}`
                                    : 'Ben Leven, MSN, PMHNP-BC'}
                            </div>
                        </div>
                        <h1 className={s.heading}>
                            {practiceName
                                ? `${providerName} has invited you to join ${practiceName}.`
                                : `${providerName} has invited you to join their practice.`}
                        </h1>
                        <p className={s.lead}>{roleContent.lead}</p>
                        <ul className={s.bullets}>
                            <li>
                                {inviteInfo?.isExistingUser
                                    ? 'Sign in to activate your account.'
                                    : 'Create a password to activate your account.'}
                            </li>
                            {roleContent.bullets.map((bullet) => (
                                <li key={bullet}>{bullet}</li>
                            ))}
                        </ul>
                    </div>
                    <div className={s.rightSection}>
                        <ActivateForm
                            providerName={providerName}
                            token={token}
                            initialEmail={inviteInfo?.invitee?.email || ''}
                            initialFirstName={inviteInfo?.invitee?.firstName || ''}
                            initialLastName={inviteInfo?.invitee?.lastName || ''}
                            isExistingUser={inviteInfo?.isExistingUser ?? false}
                            emailLocked={!!inviteInfo?.invitee?.email}
                        />
                    </div>
                </div>
            </Spin>
        </div>
    );
};

export default ActivateAccountPage;
